import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const APP_CONFIG = {
  'qmax-guwahati': {
    name: 'QMAX Guwahati',
    type: 'Dealer',
    location: 'Guwahati',
    packageId: 'com.sanibiz.qmaxguwahati',
    isDealer: true,
  },
  'qmax-silchar': {
    name: 'QMAX Silchar',
    type: 'Dealer',
    location: 'Silchar',
    packageId: 'com.qmaxsilchar',
    isDealer: true,
  },
  'qmax-guwahati-salesperson': {
    name: 'QMAX Guwahati Salesperson',
    type: 'Salesperson',
    location: 'Guwahati',
    packageId: 'com.qmaxguwahati.salesperson',
    isDealer: false,
  },
  'qmax-silchar-salesperson': {
    name: 'QMAX Silchar Salesperson',
    type: 'Salesperson',
    location: 'Silchar',
    packageId: 'com.qmaxsilchar.salesperson',
    isDealer: false,
  },
};

const AppTermsOfUse = () => {
  const { appSlug } = useParams();
  const app = APP_CONFIG[appSlug];

  if (!app) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="mb-8">
              <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
                ← Back to Home
              </Link>
            </div>

            {/* App Badge */}
            <div className="mb-4">
              <span className="inline-block bg-blue-100 text-blue-700 text-sm font-semibold px-3 py-1 rounded-full">
                {app.name} — {app.type} App
              </span>
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Use</h1>
            <p className="text-gray-500 mb-8">Last updated: March 2026</p>

            <div className="prose prose-lg max-w-none text-gray-700">

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                <p className="mb-4">
                  Welcome to the <strong>{app.name}</strong> {app.type} App (Package ID: <code>{app.packageId}</code>).
                  These Terms of Use ("Terms") govern your access to and use of this application.
                </p>
                <p className="mb-4">
                  By downloading, installing, or using this App, you agree to be bound by these Terms. If you do not agree
                  to these Terms, please do not use the App.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
                <p className="mb-4">The {app.name} App provides the following services:</p>
                {app.isDealer ? (
                  <ul className="list-disc pl-6 mb-4 space-y-2">
                    <li>Browse the {app.location} QMAX tile catalog with product specifications, images, and dimensions</li>
                    <li>Request price quotes for tiles and construction materials</li>
                    <li>Track order status and view order history</li>
                    <li>Access dealer-specific pricing and promotional offers</li>
                    <li>Communicate with the {app.location} sales team</li>
                  </ul>
                ) : (
                  <ul className="list-disc pl-6 mb-4 space-y-2">
                    <li>Manage and respond to dealer quote requests for the {app.location} region</li>
                    <li>Scan product QR codes to retrieve real-time tile information</li>
                    <li>Access live inventory data for the {app.location} warehouse</li>
                    <li>View and manage dealer accounts and order histories</li>
                    <li>Generate and share product quotes and summaries</li>
                  </ul>
                )}
                <p className="mb-4">
                  This App is connected to the SANIBIZ MIS backend system and requires valid user credentials for access.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Registration and Accounts</h2>
                <p className="mb-4">
                  To use the {app.name} App, you must be {app.isDealer ? 'a registered QMAX dealer' : 'an authorized QMAX sales team member'} with valid login credentials.
                  You are responsible for:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Immediately notifying us of any unauthorized use of your account</li>
                  <li>Ensuring your account information is accurate and up-to-date</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Acceptable Use</h2>
                <p className="mb-4">You agree not to:</p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Use the App for any illegal or unauthorized purpose</li>
                  <li>Share your login credentials with unauthorized persons</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with or disrupt the App or servers</li>
                  <li>Reverse engineer, decompile, or disassemble the App</li>
                  <li>Use the App to transmit harmful code or malware</li>
                  <li>Scrape or collect data from the App without permission</li>
                  {!app.isDealer && <li>Use the QR scanner or camera for any purpose other than official QMAX operations</li>}
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Pricing and Quotes</h2>
                <p className="mb-4">
                  All prices displayed in the App are indicative and subject to change. Quote requests are processed by the
                  {app.location} sales team and final pricing will be communicated through official channels. We reserve the right to:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Modify prices without prior notice</li>
                  <li>Accept or reject quote requests at our discretion</li>
                  <li>Apply specific terms and conditions to accepted quotes</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
                <p className="mb-4">
                  All content in the App, including but not limited to text, graphics, logos, images, product information,
                  and software, is the property of QMAX or its licensors and is protected by intellectual property laws.
                </p>
                <p className="mb-4">
                  You may not copy, reproduce, distribute, or create derivative works from any content without express written permission.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data and Privacy</h2>
                <p className="mb-4">
                  Your use of the App is also governed by our{' '}
                  <Link to={`/${appSlug}/privacy-policy`} className="text-blue-600 hover:underline">Privacy Policy</Link>,
                  which describes how we collect, use, and protect your personal information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Disclaimer of Warranties</h2>
                <p className="mb-4">
                  THE APP IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE THAT THE APP WILL BE
                  UNINTERRUPTED, ERROR-FREE, OR SECURE. PRODUCT IMAGES AND SPECIFICATIONS ARE FOR REFERENCE ONLY AND MAY
                  VARY FROM ACTUAL PRODUCTS.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
                <p className="mb-4">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, QMAX AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY
                  INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE APP.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Termination</h2>
                <p className="mb-4">
                  We reserve the right to terminate or suspend your access to the App at any time, with or without cause,
                  and with or without notice. Upon termination, your right to use the App will immediately cease.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
                <p className="mb-4">
                  We may update these Terms from time to time. Continued use of the App after changes constitutes acceptance
                  of the modified Terms. We encourage you to review these Terms periodically.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
                <p className="mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising
                  from these Terms shall be subject to the exclusive jurisdiction of the courts in {app.location}, Assam.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
                <p className="mb-4">
                  If you have any questions about these Terms, please contact us:
                </p>
                <ul className="list-none space-y-2">
                  <li>📧 Email: <a href="mailto:info@starsolutions.biz" className="text-blue-600 hover:underline">info@starsolutions.biz</a></li>
                  <li>🌐 Website: <a href="https://qmaxonline.com" className="text-blue-600 hover:underline">qmaxonline.com</a></li>
                </ul>
              </section>

            </div>

            {/* Link to Privacy Policy */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-gray-600 text-sm">
                Also see our{' '}
                <Link to={`/${appSlug}/privacy-policy`} className="text-blue-600 hover:underline font-medium">
                  Privacy Policy
                </Link>{' '}
                for the {app.name} App.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AppTermsOfUse;
